import { arrayMove } from '@dnd-kit/sortable';
import { Element, ElementContent } from 'hast';
import {
  ChartRow,
  ChartSeries,
  CompareLineChartData,
  ParsedTable,
  SortedTableRow,
  SortDirection,
  TableSort,
  VariantLineChartData,
} from '@/features/ai-agents/chat/MarkdownTableVisualization/types';

export const COMPARE_COLUMN_HEADER = `Compared To`;
export const COMPARE_CURRENT_LABEL = `Current`;

export function formatColumnHeader(header: string) {
  if (!header) {
    return ``;
  }

  const withSpaces = header.replace(/[-_]+/g, ` `).trim();

  if (!withSpaces) {
    return ``;
  }

  return withSpaces.charAt(0).toUpperCase() + withSpaces.slice(1);
}

export function parseMarkdownTable(node: Element): ParsedTable {
  const head = findChildElement(node, `thead`);
  const body = findChildElement(node, `tbody`);
  const headerRow = head ? findChildElements(head, `tr`)[0] : undefined;
  const headers = headerRow
    ? findChildElements(headerRow, `th`).map(extractText)
    : [];
  const rows = body
    ? findChildElements(body, `tr`).map((row) =>
        findChildElements(row, `td`).map(extractText),
      )
    : [];

  return { headers, rows };
}

export function parseNumericValue(value: string) {
  const trimmedValue = value.trim();
  const numericPattern =
    /^\(?[+-]?(?:[$€£¥₹]\s*)?(?:\d+|\d{1,3}(?:,\d{3})+)(?:\.\d+)?%?\)?$/;

  if (!numericPattern.test(trimmedValue)) {
    return null;
  }

  const isNegative = trimmedValue.startsWith(`(`);
  const valueWithoutFormatting = trimmedValue.replace(/[^\d.+-]/g, ``);
  const parsedValue = Number(valueWithoutFormatting);

  if (!Number.isFinite(parsedValue)) {
    return null;
  }

  return isNegative ? -parsedValue : parsedValue;
}

const NUMERIC_CELL_PATTERN =
  /^(\()?([+-])?([$€£¥₹]\s*)?((?:\d+|\d{1,3}(?:,\d{3})+))(\.\d+)?(%)?(\))?$/;

export function formatNumericCellValue(value: string) {
  const trimmedValue = value.trim();
  const match = trimmedValue.match(NUMERIC_CELL_PATTERN);

  if (!match) {
    return value;
  }

  const [
    ,
    openParen = ``,
    sign = ``,
    currency = ``,
    integerPart,
    decimalPart = ``,
    percentSign = ``,
    closeParen = ``,
  ] = match;
  const integerWithoutCommas = integerPart.replace(/,/g, ``);
  const integerWithCommas =
    Number(integerWithoutCommas).toLocaleString(`en-US`);

  return `${openParen}${sign}${currency}${integerWithCommas}${decimalPart}${percentSign}${closeParen}`;
}

export function sortTableRows(
  rows: string[][],
  columnIndex?: number,
  direction?: SortDirection,
): SortedTableRow[] {
  const rowsWithOriginalIndexes = rows.map((values, originalIndex) => ({
    originalIndex,
    values,
  }));

  if (columnIndex === undefined || !direction) {
    return rowsWithOriginalIndexes;
  }

  return rowsWithOriginalIndexes.sort((firstRow, secondRow) => {
    const result = compareTableValues(
      firstRow.values[columnIndex] ?? ``,
      secondRow.values[columnIndex] ?? ``,
      direction,
    );

    return result || firstRow.originalIndex - secondRow.originalIndex;
  });
}

export function reorderColumns(
  columnOrder: number[],
  activeColumnIndex: number,
  overColumnIndex: number,
) {
  const oldIndex = columnOrder.indexOf(activeColumnIndex);
  const newIndex = columnOrder.indexOf(overColumnIndex);

  if (oldIndex < 0 || newIndex < 0) {
    return columnOrder;
  }

  return arrayMove(columnOrder, oldIndex, newIndex);
}

function compareTableValues(
  firstValue: string,
  secondValue: string,
  direction: SortDirection,
) {
  const firstIsMissing = isMissingTableValue(firstValue);
  const secondIsMissing = isMissingTableValue(secondValue);

  if (firstIsMissing || secondIsMissing) {
    if (firstIsMissing && secondIsMissing) {
      return 0;
    }

    return firstIsMissing ? 1 : -1;
  }

  const firstNumber = parseNumericValue(firstValue);
  const secondNumber = parseNumericValue(secondValue);
  const comparison =
    firstNumber !== null && secondNumber !== null
      ? firstNumber - secondNumber
      : firstValue.localeCompare(secondValue, undefined, {
          numeric: true,
          sensitivity: `base`,
        });

  return direction === `ascending` ? comparison : -comparison;
}

export function getNextSort(
  currentSort: TableSort | null,
  columnIndex: number,
): TableSort | null {
  if (currentSort?.columnIndex !== columnIndex) {
    return { columnIndex, direction: `descending` };
  }

  if (currentSort.direction === `descending`) {
    return { columnIndex, direction: `ascending` };
  }

  return null;
}

export function getCurrentColumnOrder(
  columnOrder: number[],
  columnCount: number,
) {
  const availableColumnIndexes = Array.from(
    { length: columnCount },
    (_value, index) => index,
  );
  const currentColumnIndexes = columnOrder.filter((index) =>
    availableColumnIndexes.includes(index),
  );
  const newColumnIndexes = availableColumnIndexes.filter(
    (index) => !currentColumnIndexes.includes(index),
  );

  return [...currentColumnIndexes, ...newColumnIndexes];
}

/**
 * Converts a persisted, column-name-keyed order (stable across column-set
 * changes) back into index-based `columnOrder` for in-memory use. Unknown
 * names are dropped; any headers missing from the stored order are
 * reconciled by the caller via `getCurrentColumnOrder`.
 */
export function resolveHintedColumnOrder(
  headers: string[],
  columnOrder: string[] | null,
): number[] | null {
  if (!columnOrder?.length) {
    return null;
  }

  const resolvedIndexes = columnOrder
    .map((name) => headers.indexOf(name))
    .filter((index) => index >= 0);

  return resolvedIndexes.length > 0 ? resolvedIndexes : null;
}

/**
 * Converts a persisted, column-name-keyed sort back into an index-based
 * `TableSort` for in-memory use. Falls back to `null` (unsorted) if the
 * named column no longer exists.
 */
export function resolveHintedSort(
  headers: string[],
  sort: { column: string; direction: SortDirection } | null,
): TableSort | null {
  if (!sort) {
    return null;
  }

  const columnIndex = headers.indexOf(sort.column);

  return columnIndex >= 0 ? { columnIndex, direction: sort.direction } : null;
}

/**
 * Converts a persisted, column-name-keyed width map back into an
 * index-based `columnWidths` map for in-memory use. Unknown names are
 * dropped.
 */
export function resolveHintedColumnWidths(
  headers: string[],
  columnWidths: Record<string, number> | null,
): Record<number, number> | null {
  if (!columnWidths) {
    return null;
  }

  const resolved = Object.entries(columnWidths).reduce<Record<number, number>>(
    (normalized, [name, width]) => {
      const columnIndex = headers.indexOf(name);
      if (columnIndex >= 0) {
        normalized[columnIndex] = width;
      }
      return normalized;
    },
    {},
  );

  return Object.keys(resolved).length > 0 ? resolved : null;
}

export function findVariantColumnIndex(headers: string[]) {
  return headers.findIndex(
    (header) => header.trim().toLowerCase() === `variant`,
  );
}

export function findCompareColumnIndex(headers: string[]) {
  return headers.findIndex(
    (header) =>
      header.trim().toLowerCase() === COMPARE_COLUMN_HEADER.toLowerCase(),
  );
}

export function filterCompareCurrentRows(
  rows: string[][],
  compareColumnIndex: number,
): string[][] {
  if (compareColumnIndex < 0) {
    return rows;
  }

  return rows.filter(
    (row) =>
      (row[compareColumnIndex]?.trim().toLowerCase() ?? ``) ===
      COMPARE_CURRENT_LABEL.toLowerCase(),
  );
}

export function findColumnIndexByLabel(
  headers: string[],
  label: string | null,
) {
  if (!label) {
    return -1;
  }

  const normalizedLabel = normalizeHeaderLabel(label);
  const normalizedHeaders = headers.map(normalizeHeaderLabel);

  const exactMatchIndex = normalizedHeaders.findIndex(
    (header) => header === normalizedLabel,
  );

  if (exactMatchIndex >= 0) {
    return exactMatchIndex;
  }

  return normalizedHeaders.findIndex(
    (header) =>
      header.length > 0 &&
      normalizedLabel.length > 0 &&
      (header.includes(normalizedLabel) || normalizedLabel.includes(header)),
  );
}

function normalizeHeaderLabel(value: string) {
  return value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ``);
}

export function resolveTooltipText(
  tooltips: Record<string, string> | null,
  label: string | null | undefined,
) {
  if (!tooltips || !label) {
    return null;
  }

  const normalizedLabel = normalizeHeaderLabel(label);

  if (!normalizedLabel) {
    return null;
  }

  const tooltipEntries = Object.entries(tooltips);
  const exactMatch = tooltipEntries.find(
    ([tooltipLabel]) => normalizeHeaderLabel(tooltipLabel) === normalizedLabel,
  );

  if (exactMatch) {
    return exactMatch[1];
  }

  const partialMatch = tooltipEntries.find(([tooltipLabel]) => {
    const normalizedTooltipLabel = normalizeHeaderLabel(tooltipLabel);

    return (
      normalizedTooltipLabel.length > 0 &&
      (normalizedTooltipLabel.includes(normalizedLabel) ||
        normalizedLabel.includes(normalizedTooltipLabel))
    );
  });

  return partialMatch ? partialMatch[1] : null;
}

export function findDefaultXAxisColumnIndex(
  columnCount: number,
  numericColumnIndexes: number[],
  excludedColumnIndexes: number[],
) {
  const columnIndexes = Array.from(
    { length: columnCount },
    (_value, index) => index,
  ).filter((index) => !excludedColumnIndexes.includes(index));

  return (
    columnIndexes.find((index) => !numericColumnIndexes.includes(index)) ??
    columnIndexes[0] ??
    0
  );
}

export function findNumericColumnIndexes(table: ParsedTable) {
  return table.headers.reduce<number[]>((indexes, _header, columnIndex) => {
    const values = table.rows
      .map((row) => row[columnIndex]?.trim())
      .filter(Boolean);
    let isNumericColumn =
      values.length > 0 &&
      values.some((value) => parseNumericValue(value) !== null) &&
      values.every(
        (value) =>
          parseNumericValue(value) !== null || isMissingTableValue(value),
      );

    if (_header?.toLowerCase() === `rank`) {
      isNumericColumn = false;
    }

    return isNumericColumn ? [...indexes, columnIndex] : indexes;
  }, []);
}

function isMissingTableValue(value: string) {
  const normalizedValue = value.trim().toLowerCase();

  return (
    !normalizedValue || [`-`, `—`, `n/a`, `null`].includes(normalizedValue)
  );
}

export function createChartData(
  rows: string[][],
  xColumnIndex: number,
  yColumnIndex: number,
): ChartRow[] {
  return rows.reduce<ChartRow[]>((data, row) => {
    const formattedY = row[yColumnIndex] ?? ``;
    const y = parseNumericValue(formattedY);

    if (y === null) {
      return data;
    }

    return [...data, { formattedY, x: row[xColumnIndex] ?? ``, y }];
  }, []);
}

const HIGH_VALUE_COUNT_THRESHOLD = 6;
const HIGH_VALUE_COUNT_TICK_INTERVAL = 3;
const LOW_VALUE_COUNT_TICK_INTERVAL = 0;
const MIN_VISIBLE_X_AXIS_TICKS = 2;

// Narrowest a tick label can get before adjacent labels start colliding.
// Keeps this in step with `X_AXIS_LABEL_CHARACTER_WIDTH` in helpers.tsx,
// sized for a short wrapped label (a couple of characters per line).
export const MIN_X_AXIS_TICK_WIDTH_PX = 56;

/**
 * Determines the x-axis tick interval for bar/line charts so labels avoid
 * colliding while showing as many as will fit. When the chart's rendered
 * width is known, the interval is derived from how many labels can actually
 * fit in that width; otherwise it falls back to a static, row-count-based
 * rule (e.g. before the container has been measured).
 * @param valueCount - Number of values (rows) rendered on the chart
 * @param chartWidth - Current rendered width (px) of the chart, if known
 * @returns The tick interval to pass to Recharts' XAxis
 */
export function getChartXAxisInterval(valueCount: number, chartWidth?: number) {
  if (!chartWidth) {
    return valueCount > HIGH_VALUE_COUNT_THRESHOLD
      ? HIGH_VALUE_COUNT_TICK_INTERVAL
      : LOW_VALUE_COUNT_TICK_INTERVAL;
  }

  const maxVisibleTicks = Math.max(
    MIN_VISIBLE_X_AXIS_TICKS,
    Math.floor(chartWidth / MIN_X_AXIS_TICK_WIDTH_PX),
  );

  if (valueCount <= maxVisibleTicks) {
    return LOW_VALUE_COUNT_TICK_INTERVAL;
  }

  return Math.ceil(valueCount / maxVisibleTicks) - 1;
}

export function sortChartRowsByDate<T extends { x: string }>(rows: T[]): T[] {
  const rowsWithDates = rows.map((row, index) => ({
    date: parseChartDate(row.x),
    index,
    row,
  }));
  const populatedRows = rowsWithDates.filter(({ row }) => row.x.trim());
  const hasDateXAxis =
    populatedRows.length > 0 &&
    populatedRows.every(({ date }) => date !== null);

  if (!hasDateXAxis) {
    return rows;
  }

  return rowsWithDates
    .sort((firstRow, secondRow) => {
      if (firstRow.date === null || secondRow.date === null) {
        if (firstRow.date === null && secondRow.date === null) {
          return firstRow.index - secondRow.index;
        }

        return firstRow.date === null ? 1 : -1;
      }

      return firstRow.date - secondRow.date || firstRow.index - secondRow.index;
    })
    .map(({ row }) => row);
}

function parseChartDate(value: string) {
  const normalizedValue = value.trim().replace(/\s*\([^)]*\)\s*$/, ``);

  if (!isChartDateFormat(normalizedValue)) {
    return null;
  }

  const parsedDate = Date.parse(normalizedValue);

  return Number.isFinite(parsedDate) ? parsedDate : null;
}

function isChartDateFormat(value: string) {
  const monthName =
    `(?:jan(?:uary)?|feb(?:ruary)?|mar(?:ch)?|apr(?:il)?|may|jun(?:e)?|` +
    `jul(?:y)?|aug(?:ust)?|sep(?:tember)?|oct(?:ober)?|nov(?:ember)?|dec(?:ember)?)`;

  return [
    /^\d{4}[-/.]\d{1,2}(?:[-/.]\d{1,2})?(?:$|[T\s])/,
    /^\d{1,2}[-/.]\d{1,2}[-/.]\d{2,4}(?:$|\s)/,
    new RegExp(`^${monthName}\\s+\\d{1,2}(?:,)?\\s+\\d{4}(?:$|\\s)`, `i`),
    new RegExp(`^\\d{1,2}\\s+${monthName}\\s+\\d{4}(?:$|\\s)`, `i`),
  ].some((pattern) => pattern.test(value));
}

export function createVariantChartSeries(
  rows: string[][],
  variantColumnIndex: number,
  xColumnIndex: number,
  yColumnIndex: number,
) {
  const seriesByVariant = new Map<string, ChartSeries>();

  rows.forEach((row) => {
    const variantName = row[variantColumnIndex]?.trim();

    if (!variantName) {
      return;
    }

    const variantKey = variantName.toLowerCase();
    const existingSeries = seriesByVariant.get(variantKey) ?? {
      data: [],
      name: variantName,
    };
    const formattedY = row[yColumnIndex] ?? ``;
    const y = parseNumericValue(formattedY);

    if (y !== null) {
      existingSeries.data.push({
        formattedY,
        x: row[xColumnIndex] ?? ``,
        y,
      });
    }

    seriesByVariant.set(variantKey, existingSeries);
  });

  return Array.from(seriesByVariant.values());
}

export function createVariantLineChartData(
  chartSeries: ChartSeries[],
): VariantLineChartData {
  const alignedSeries = alignVariantSeriesXValues(chartSeries);
  const series = alignedSeries.map((item, index) => ({
    dataKey: `variant_${index}`,
    name: item.name,
  }));
  const rowsByXValue = new Map<
    string,
    { x: string } & Record<string, number | string>
  >();

  alignedSeries.forEach((item, seriesIndex) => {
    item.data.forEach((point) => {
      const x = point.x.trim();

      if (!x) {
        return;
      }

      const row = rowsByXValue.get(x) ?? { x };
      row[series[seriesIndex].dataKey] = point.y;
      rowsByXValue.set(x, row);
    });
  });

  return { rows: Array.from(rowsByXValue.values()), series };
}

export function createCompareChartSeries(
  rows: string[][],
  compareColumnIndex: number,
  xColumnIndex: number,
  yColumnIndex: number,
): { compare: ChartSeries | null; current: ChartSeries } {
  const seriesByLabel = new Map<string, ChartSeries>();

  rows.forEach((row) => {
    const label = row[compareColumnIndex]?.trim();

    if (!label) {
      return;
    }

    const labelKey = label.toLowerCase();
    const existingSeries = seriesByLabel.get(labelKey) ?? {
      data: [],
      name: label,
    };
    const formattedY = row[yColumnIndex] ?? ``;
    const y = parseNumericValue(formattedY);

    if (y !== null) {
      existingSeries.data.push({ formattedY, x: row[xColumnIndex] ?? ``, y });
    }

    seriesByLabel.set(labelKey, existingSeries);
  });

  const currentKey = COMPARE_CURRENT_LABEL.toLowerCase();
  const current = seriesByLabel.get(currentKey) ?? {
    data: [],
    name: COMPARE_CURRENT_LABEL,
  };
  const compare =
    Array.from(seriesByLabel.entries()).find(
      ([labelKey]) => labelKey !== currentKey,
    )?.[1] ?? null;

  return { compare, current };
}

// Aligns the compare series onto the current series by relative day position
// (rather than by literal date match) so the two periods overlay on a shared
// x-axis instead of appearing as disjoint segments.
export function createCompareLineChartData(
  current: ChartSeries,
  compare: ChartSeries | null,
): CompareLineChartData {
  const sortedCurrent = sortChartRowsByDate(current.data);

  if (!compare) {
    return {
      rows: sortedCurrent.map((point) => ({ current: point.y, x: point.x })),
      series: [{ dataKey: `current`, name: current.name }],
    };
  }

  const sortedCompare = sortChartRowsByDate(compare.data);
  const rows = sortedCurrent.map((point, index) => {
    const comparePoint = sortedCompare[index];
    const row: { x: string } & Record<string, number | string> = {
      current: point.y,
      x: point.x,
    };

    if (comparePoint) {
      row.compare = comparePoint.y;
    }

    return row;
  });

  return {
    rows,
    series: [
      { dataKey: `current`, name: current.name },
      { dashed: true, dataKey: `compare`, name: compare.name },
    ],
  };
}

function alignVariantSeriesXValues(chartSeries: ChartSeries[]) {
  const referenceSeries = chartSeries.reduce<ChartSeries | undefined>(
    (currentReference, item) => {
      if (!currentReference) {
        return item;
      }

      return countDistinctXValues(item) > countDistinctXValues(currentReference)
        ? item
        : currentReference;
    },
    undefined,
  );
  const referenceXValues = referenceSeries?.data.map((point) => point.x) ?? [];
  const referenceDistinctXCount = referenceSeries
    ? countDistinctXValues(referenceSeries)
    : 0;

  return chartSeries.map((item) => {
    const shouldUseReferenceXValues =
      item !== referenceSeries &&
      referenceDistinctXCount > 1 &&
      countDistinctXValues(item) <= 1 &&
      referenceXValues.length >= item.data.length;

    if (!shouldUseReferenceXValues) {
      return item;
    }

    return {
      ...item,
      data: item.data.map((point, index) => ({
        ...point,
        x: referenceXValues[index],
      })),
    };
  });
}

function countDistinctXValues(chartSeries: ChartSeries) {
  return new Set(
    chartSeries.data.map((point) => point.x.trim()).filter(Boolean),
  ).size;
}

function findChildElement(node: Element, tagName: string) {
  return findChildElements(node, tagName)[0];
}

function findChildElements(node: Element, tagName: string) {
  return node.children.filter(
    (child): child is Element =>
      child.type === `element` && child.tagName === tagName,
  );
}

function extractText(node: ElementContent): string {
  if (node.type === `text`) {
    return node.value;
  }

  if (node.type !== `element`) {
    return ``;
  }

  return node.children.map(extractText).join(``).replace(/\s+/g, ` `).trim();
}
